import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const SCurveExport = ({ sCurveData, projectName }) => {
  // Handle export to Excel
  const handleExportToExcel = () => {
    if (!sCurveData) return;
    
    try {
      // Create export data
      const exportData = [];
      
      // Add header row
      exportData.push([
        'Date/Period',
        'Planned Progress (%)',
        'Actual Progress (%)',
        'Variance (%)'
      ]);
      
      // Add data rows
      const { labels, datasets } = sCurveData.chartData;
      const plannedData = datasets[0].data;
      const actualData = datasets[1].data;
      
      labels.forEach((label, index) => {
        const planned = plannedData[index] || 0;
        const actual = actualData[index] || 0;
        const variance = actual - planned;
        
        exportData.push([
          label,
          planned,
          actual,
          variance
        ]);
      });
      
      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(exportData);
      
      // Set column widths
      const wscols = [
        { wch: 15 },  // Date/Period
        { wch: 20 },  // Planned Progress
        { wch: 20 },  // Actual Progress
        { wch: 15 }   // Variance
      ];
      worksheet['!cols'] = wscols;
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'S-Curve Data');
      
      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Save file
      saveAs(blob, `${projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_scurve_data.xlsx`);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
    }
  };
  
  // Handle export to CSV
  const handleExportToCSV = () => {
    if (!sCurveData) return;
    
    try {
      // Create CSV content
      let csvContent = 'Date/Period,Planned Progress (%),Actual Progress (%),Variance (%)\n';
      
      // Add data rows
      const { labels, datasets } = sCurveData.chartData;
      const plannedData = datasets[0].data;
      const actualData = datasets[1].data;
      
      labels.forEach((label, index) => {
        const planned = plannedData[index] || 0;
        const actual = actualData[index] || 0;
        const variance = actual - planned;
        
        csvContent += `"${label}",${planned},${actual},${variance}\n`;
      });
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `${projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_scurve_data.csv`);
    } catch (err) {
      console.error('Error exporting to CSV:', err);
    }
  };
  
  // Handle export to PNG
  const handleExportToPNG = () => {
    if (!sCurveData) return;
    
    try {
      // Find the chart canvas
      const canvas = document.querySelector('canvas');
      if (!canvas) {
        console.error('Canvas element not found');
        return;
      }
      
      // Convert canvas to PNG
      canvas.toBlob(function(blob) {
        saveAs(blob, `${projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_scurve_chart.png`);
      });
    } catch (err) {
      console.error('Error exporting to PNG:', err);
    }
  };
  
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#5D4037' }}>
        Export S-Curve Data
      </Typography>
      
      {!sCurveData ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No S-Curve data available to export. Please generate S-Curve data first.
        </Alert>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            You can export the S-Curve data in different formats. Choose one of the options below.
          </Alert>
          
          <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportToExcel}
              sx={{ 
                color: '#5D4037', 
                borderColor: '#5D4037',
                '&:hover': { 
                  backgroundColor: '#F9F5EB',
                  borderColor: '#8D6E63'
                }
              }}
            >
              Export to Excel
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportToCSV}
              sx={{ 
                color: '#5D4037', 
                borderColor: '#5D4037',
                '&:hover': { 
                  backgroundColor: '#F9F5EB',
                  borderColor: '#8D6E63'
                }
              }}
            >
              Export to CSV
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportToPNG}
              sx={{ 
                color: '#5D4037', 
                borderColor: '#5D4037',
                '&:hover': { 
                  backgroundColor: '#F9F5EB',
                  borderColor: '#8D6E63'
                }
              }}
            >
              Export Chart as PNG
            </Button>
          </Box>
          
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            Preview Data
          </Typography>
          
          <TableContainer component={Paper} elevation={0} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F9F5EB' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date/Period</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Planned Progress (%)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actual Progress (%)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Variance (%)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sCurveData.chartData.labels.map((label, index) => {
                  const planned = sCurveData.chartData.datasets[0].data[index] || 0;
                  const actual = sCurveData.chartData.datasets[1].data[index] || 0;
                  const variance = actual - planned;
                  
                  return (
                    <TableRow key={index} hover>
                      <TableCell>{label}</TableCell>
                      <TableCell>{planned}%</TableCell>
                      <TableCell>{actual}%</TableCell>
                      <TableCell sx={{ 
                        color: variance >= 0 ? '#4CAF50' : '#F44336',
                        fontWeight: 'bold'
                      }}>
                        {variance > 0 ? '+' : ''}{variance.toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default SCurveExport;
